/**
 * SyncProvider + useSync (CONTRACTS.md §3).
 *
 * - `online` comes from navigator.onLine plus the browser online/offline
 *   events — it reflects connectivity only, never write success.
 * - `pendingWrites` is the live size of the persisted offline queue
 *   ('rust-sync-queue'); it updates on queue changes (same-tab custom event
 *   + cross-tab storage events).
 * - On reconnect (and once on mount when online) the queue is flushed FIFO
 *   through registered handlers (registerSyncHandler). Flush is serialized
 *   and debounce-protected (a single in-flight flush; rapid online/offline
 *   flapping collapses into one pass).
 *
 * The provider never claims a cloud save: repositories do that after the
 * server confirms. The queue only retries failed/deferred writes.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { isCloudConfigured } from '@/lib/supabase/client'
import {
  clearQueueOverflow,
  loadQueue,
  markWriteAttempt,
  MAX_QUEUE_ATTEMPTS,
  pendingWriteCount,
  removeWrite,
  SYNC_QUEUE_KEY,
  type QueuedWrite,
} from './queue'

export type SyncHandler = (payload: unknown) => Promise<void>

const handlers = new Map<string, SyncHandler>()

/**
 * Registers the executor for a queued write kind. Feature modules call this
 * once (module scope) so queued writes survive a reload.
 */
export function registerSyncHandler(kind: string, handler: SyncHandler): void {
  handlers.set(kind, handler)
}

export type SyncContextValue = {
  online: boolean
  pendingWrites: number
}

const SyncContext = createContext<SyncContextValue>({ online: true, pendingWrites: 0 })

const RETRY_DELAY_MS = 15_000

function readOnline(): boolean {
  return typeof navigator === 'undefined' ? true : navigator.onLine
}

export function SyncProvider({ children }: { children: ReactNode }) {
  const [online, setOnline] = useState<boolean>(readOnline)
  const [pendingWrites, setPendingWrites] = useState<number>(() => pendingWriteCount())
  const flushingRef = useRef(false)
  const flushAgainRef = useRef(false)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const refreshCount = useCallback(() => {
    setPendingWrites(pendingWriteCount())
  }, [])

  const flush = useCallback(async () => {
    if (flushingRef.current) {
      flushAgainRef.current = true
      return
    }
    if (!readOnline() || !isCloudConfigured()) return
    flushingRef.current = true
    try {
      // Loop (not recursion) so a flush requested mid-pass reruns immediately.
      do {
        flushAgainRef.current = false
        for (;;) {
          const state = loadQueue()
          const next: QueuedWrite | undefined = state.entries.find((entry) => entry.attempts < MAX_QUEUE_ATTEMPTS)
          if (!next) break
          const handler = handlers.get(next.kind)
          if (!handler) {
            // No executor registered (feature not loaded yet): keep the entry
            // and try again later — never drop it silently.
            markWriteAttempt(next.id, 'no handler registered')
            continue
          }
          try {
            await handler(next.payload)
            removeWrite(next.id)
            clearQueueOverflow()
            setPendingWrites(pendingWriteCount())
          } catch (error) {
            const message = error instanceof Error ? error.message : 'flush failed'
            markWriteAttempt(next.id, message.slice(0, 300))
            setPendingWrites(pendingWriteCount())
            // Stop the pass on failure: FIFO order is preserved and the entry
            // is retried on the next online event or the retry timer.
            break
          }
        }
      } while (flushAgainRef.current)
    } finally {
      flushingRef.current = false
    }
  }, [])

  const scheduleRetry = useCallback(() => {
    if (retryTimerRef.current) return
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null
      if (readOnline() && pendingWriteCount() > 0) void flush()
    }, RETRY_DELAY_MS)
  }, [flush])

  // Connectivity events: retry-on-reconnect.
  useEffect(() => {
    const handleOnline = () => {
      setOnline(true)
      void flush()
    }
    const handleOffline = () => setOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [flush])

  // Queue bookkeeping events (same-tab + cross-tab).
  useEffect(() => {
    const handleQueueChanged = () => {
      refreshCount()
      if (readOnline()) void flush()
      scheduleRetry()
    }
    const handleStorage = (event: StorageEvent) => {
      if (event.key === SYNC_QUEUE_KEY || event.key === null) handleQueueChanged()
    }
    window.addEventListener('rust-sync-queue-changed', handleQueueChanged)
    window.addEventListener('storage', handleStorage)
    return () => {
      window.removeEventListener('rust-sync-queue-changed', handleQueueChanged)
      window.removeEventListener('storage', handleStorage)
    }
  }, [flush, refreshCount, scheduleRetry])

  // One flush attempt on mount when online and work is pending.
  useEffect(() => {
    if (readOnline() && pendingWriteCount() > 0) void flush()
    if (pendingWriteCount() > 0) scheduleRetry()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(
    () => () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current)
    },
    [],
  )

  const value = useMemo<SyncContextValue>(() => ({ online, pendingWrites }), [online, pendingWrites])
  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>
}

export function useSync(): SyncContextValue {
  return useContext(SyncContext)
}
